import { IProfile } from '@assetmark/profile/dist/profileComponent';
import * as React from 'react';
import { defaultProfileData } from '../../constants/data.constant';
import { parseGoalTypeEnum, parseProfileResposeToIProfile } from '../../helpers/model.helper';
import { TLeadProfileResponse } from '../../models/lead-profile-response';
import { TLeadStrategyInfoResponse } from '../../models/lead-strategy-info-response';
import { TScreenType } from '../../models/screen-type';
import {
	UPDATE_LEAD_DATA,
	LEAD_SCREEN,
	LEAD_PROFILE,
	LEAD_RETIREMENT_AGE,
	LEAD_MONTHLY_INCOME,
	LEAD_RETIREMENT_INCOME,
	LEAD_ANNUAL_SAVING,
	LEAD_INVESTER_TARGET,
	UPDATE_NAV_STATE,
} from './lead.constant';
import { leadContextReducer, LeadContextState, initialState } from './lead.context.reducer';
import { TInvestorNavState } from './lead.model';
import { useGoal } from '../goal/goal.context';

type LeadProviderProps = { children: React.ReactNode };

const LeadStateContext = React.createContext<LeadContextState>(initialState);
LeadStateContext.displayName = 'LeadContext';

function LeadContextProvider({ children }: LeadProviderProps) {
	const [state, dispatch] = React.useReducer(leadContextReducer, initialState);
	const { goalAmount, goalGoalType, goalBirthYear } = useGoal();
	
	const returnPayload = (type: any, payload: any) => {
		if (type === LEAD_RETIREMENT_AGE) {
			return payload.retirementAge;
		} else if (type === LEAD_MONTHLY_INCOME) {
			return payload.MonthlyIncome;
		} else if (type === LEAD_RETIREMENT_INCOME) {
			return {
				GoalType: Number(payload.investmentReason),
				MonthlyIncome: payload.MonthlyIncome,
				MonthlyIncomeToRetirePercent: payload.MonthlyIncomeToRetirePercent,
				NetRetirementMonthlyIncome: payload.NetRetirementMonthlyIncome,
				OtherRetirementIncomes: payload.OtherRetirementIncomes,
			};
		} else if (type === LEAD_ANNUAL_SAVING) {
			return payload.AnnualSavings;
			// return { GoalType: Number(payload.investmentReason),
			// 	AnnualSavings: payload.AnnualSavings ,
			// };
		} else if (type === LEAD_INVESTER_TARGET) {
			return payload;
			// return { GoalType: Number(payload.investmentReason),
			// 	AnnualSavings: payload.AnnualSavings ,
			// };
		}
		return payload;
	};

	const setGoals = (type: any, goals: any) =>
		dispatch({
			type: type,
			payload: returnPayload(type, goals),
		});

	const setSkipGoal = (type: any, val: string) => dispatch({ type: type, payload: val });
	const setLeadProfile = (profile: IProfile) => {
		dispatch({ type: LEAD_PROFILE, payload: profile }); 
		goalAmount(profile.investmentInfo?.InvestmentAmount || 0);
		if(profile.investmentReason) {
			goalGoalType(+profile.investmentReason);
		}
	};
	const setScreen = (screen: TScreenType) => dispatch({ type: LEAD_SCREEN, payload: screen });
	const updateLeadData = (lead: TLeadProfileResponse) => { // this is unused function
		const profile = Object.assign({}, state.leadProfile, { Investor: {
			FirstName: lead.firstName.getOrElse(''),
			LastName: lead.lastName.getOrElse(''),
			EmailAddress: lead.emailAddress.getOrElse(''),
			Age: lead.age.getOrElse(0),
		 } });

		 dispatch({
				type: UPDATE_LEAD_DATA,
				payload: {
					profile,
					agentId: lead.agentId.getOrElse(''),
					leadProfileId: lead.leadProfileId.getOrElse(''),
					leadStrategyId: lead.leadStrategyId[0],
				},
			});
			goalBirthYear(new Date().getFullYear() - profile.Investor.Age);	
	};

	const updateAboutYouState = (info: TLeadStrategyInfoResponse) => {
		if(info.lead.isSome()) { // this will update AboutYou section data in context
			const leadAbout = info.lead.map(parseProfileResposeToIProfile).getOrElse(defaultProfileData);
			const profile = {
				...leadAbout,
				investmentInfo: { InvestmentAmount: info.investmentAmount.getOrElse(0) },
				investmentReason: info.goalType.map<string>(goalType => parseGoalTypeEnum(`${goalType}`)).getOrElse('0'),
			};
			const payload = {
				profile,
				agentId: info.lead.map<string>(l => l.agentId.getOrElse('')).getOrElse(''),
				leadProfileId: info.lead.map<string>(l => l.leadProfileId.getOrElse('')).getOrElse(''),
				leadStrategyId: info.leadStrategyId.getOrElse(''),
			};

			dispatch({
				type: UPDATE_LEAD_DATA,
				payload
			});
			goalAmount(info.investmentAmount.getOrElse(0));
			if(profile.investmentReason !== '0') {
				goalGoalType(+profile.investmentReason);
			}
			goalBirthYear(new Date().getFullYear() - profile.Investor.Age);
		}
	};
	const updateNavState = (investorNavState: TInvestorNavState) => dispatch({ type: UPDATE_NAV_STATE, payload: investorNavState });

	const values = React.useMemo(
		() => ({ ...state, setGoals, setSkipGoal, setLeadProfile, setScreen, updateLeadData, updateNavState, updateAboutYouState }),
		[state],
	);

	return <LeadStateContext.Provider value={{ ...values }}>{children}</LeadStateContext.Provider>;
}

function useLead() {
	const context = React.useContext(LeadStateContext);
	if (context === undefined) {
		throw new Error('useLead must be used within a LeadContextprovider');
	}
	return context;
}

export { LeadContextProvider, useLead };
